import { Children, type JSX, type PropsWithChildren, useState } from 'react';

import { DotMenuIcon } from '@dynatrace/strato-icons';

import { useCellAndColumnActions } from './useCellActions.js';
import * as styles from './UserActions.sty.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import {
  DATA_TABLE_CELL_HORIZONTAL_PADDING_MAP,
  DATA_TABLE_DEFAULT_ICON_SIZE,
  DATA_TABLE_SUB_ROW_EXPANDER_ICON_SIZE as SUB_ROW_EXPANDER_ICON_SIZE,
  DATA_TABLE_SUB_ROW_INDENT_GRID as SUB_ROW_INDENT_GRID,
} from '../../constants.js';
import {
  DataTableColumnContextProvider,
  useDataTable,
} from '../../contexts/DataTableContext.js';

/**
 * Three dots menu for inline cell or column actions that is revealed on hover.
 * @internal
 */
export function InlineUserActionsMenu(
  props: PropsWithChildren<{
    columnId: string;
    headerGroupOrRowId: string;
    type: 'cell' | 'header';
    'aria-label': string;
    'data-header-row'?: number;
    'data-column'?: number;
    hovered?: boolean;
    onClose?: () => void;
  }>,
): JSX.Element | undefined {
  const {
    columnId,
    headerGroupOrRowId,
    type,
    'aria-label': ariaLabel,
    'data-header-row': dataHeaderRow,
    'data-column': dataColumn,
    hovered,
    onClose,
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);

  const table = useDataTable();
  const column = table.getColumn(columnId);
  const actions = useCellAndColumnActions({
    enabled: menuOpen,
    headerGroupOrRowId,
    columnId,
    type,
  });

  const floatingMenuInfo = table.getState().floatingMenuInfo;
  const isFloatingMenuOpenForThis =
    floatingMenuInfo?.columnId === columnId &&
    ((type === 'cell' && floatingMenuInfo?.rowId === headerGroupOrRowId) ||
      (type === 'header' && floatingMenuInfo?.type === 'headerAction'));

  // Don't render the inline trigger when the floating (right-click) menu
  // is already open for this cell or column header
  if (isFloatingMenuOpenForThis) {
    return undefined;
  }

  /**
   * Calculates the indentation for the action trigger to not overlap with the sub-row expander.
   */
  function getSubrowIndentation(): number | undefined {
    if (!table.options.enableExpanding) {
      // no sub-rows, no indentation needed
      return undefined;
    }

    if (table.getSubRowIndicatorColumnId() !== columnId) {
      // we only need to indent in the leftmost column containing the sub-row expander
      return undefined;
    }

    const rowDensity = table.getVisualVariantOptions().rowDensity;
    if (type === 'header') {
      if (column?.columnDef.meta?.hasCustomHeaderRenderer) {
        // no indentation needed, custom header renderers are responsible for their own layout
        return undefined;
      }
      return (
        SUB_ROW_EXPANDER_ICON_SIZE +
        DATA_TABLE_CELL_HORIZONTAL_PADDING_MAP[rowDensity]
      );
    }

    // important: only fetch the row if we are rendering a cell action menu, getRow will throw if we try to fetch a header group
    const row = table.getRow(headerGroupOrRowId);
    return (
      row.depth * SUB_ROW_INDENT_GRID +
      SUB_ROW_EXPANDER_ICON_SIZE +
      DATA_TABLE_CELL_HORIZONTAL_PADDING_MAP[rowDensity]
    );
  }

  const triggerProps = {
    type: 'button',
    className: styles.actionButton,
    style: {
      display: menuOpen ? 'inline-block' : undefined,
      marginLeft: getSubrowIndentation(),
    },
    'aria-label': ariaLabel,
    'data-header-row': dataHeaderRow,
    'data-column': dataColumn,
    'data-trigger': 'useraction',
    children: (
      <DotMenuIcon
        style={{
          width: DATA_TABLE_DEFAULT_ICON_SIZE,
          height: DATA_TABLE_DEFAULT_ICON_SIZE,
        }}
      />
    ),
  } as const;

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (!menuOpen) {
    // Omit the button from the default render to reduce the number of rendered elements.
    if (hovered === false) {
      return;
    }

    return (
      <button
        {...triggerProps}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setMenuOpen(true);
          if (type === 'cell') {
            table.setOpenMenuRowId(headerGroupOrRowId);
          }
        }}
      />
    );
  }

  // Calculate the alignment of the opening menu based on the column alignment.
  const alignment = column?.columnDef.alignment?.includes('right')
    ? 'start'
    : 'end';

  return (
    <Menu
      open={menuOpen}
      onOpenChange={(open) => {
        if (!open && onClose) {
          onClose();
        }
        // reset the open row id when closing the menu to remove hover styles from the row
        if (!open && type === 'cell') {
          table.setOpenMenuRowId(null);
        }
        setMenuOpen(open);
      }}
      modal={false}
    >
      <Menu.Trigger>
        <button
          {...triggerProps}
          onClick={(evt) => {
            evt.stopPropagation();
          }}
        />
      </Menu.Trigger>
      {menuOpen === true ? (
        <DataTableColumnContextProvider value={column}>
          <Menu.Content alignment={alignment}>
            {/**
           Run all actions through a Children map so they will receive
           an automatic key and do not violate react key map rules.
           */}
            {Children.map(actions, (child) => child)}
          </Menu.Content>
        </DataTableColumnContextProvider>
      ) : null}
    </Menu>
  );
}
